/**
 * Shape contracts for the ``ask_user`` clarification renderer — the inert
 * half. The parsing/validation machinery that used to sit beside these types
 * moved to `data/harness/ask-user-details.ts` at ARCFE-1059.
 *
 * The split was forced by a real finding, not tidiness: this file arrived in
 * `/contracts` because two contracts modules need `ASK_USER_APPROVAL_KIND`,
 * and it brought 200 lines of parse/build logic with it. The duplicate-key
 * guard (ARCFE-1058) then fired on `OTHER_WIRE_VALUE = 'other'` colliding with
 * an unrelated `OTHER_SPECIALTY_ID` — pointing straight at logic that had no
 * business in an inert zone. Both sentinels are consumed only by `buildAnswer`,
 * so they travel with it.
 */

export const ASK_USER_APPROVAL_KIND = 'clarification';

/** The backend wire name of the clarification tool
 * (``service/tools/registrations/ask_user.py``). */
export const ASK_USER_TOOL_NAME = 'ask_user';

/** The backend wire name of the dedicated computer-login stop (same BE
 * module). It rides the exact ask_user clarification wait — same payload
 * shape (one ``login_computer`` question), same resolve path — so every
 * ask_user rendering site treats it identically via
 * ``isClarificationToolName``. */
export const REQUEST_COMPUTER_LOGIN_TOOL_NAME = 'request_computer_login';

export type ClarificationToolName =
  | typeof ASK_USER_TOOL_NAME
  | typeof REQUEST_COMPUTER_LOGIN_TOOL_NAME;

/** Whether a tool_use name renders on the ask_user clarification surface. */
export const isClarificationToolName = (name: string): name is ClarificationToolName =>
  name === ASK_USER_TOOL_NAME || name === REQUEST_COMPUTER_LOGIN_TOOL_NAME;

export type AskUserKind =
  | 'choice'
  | 'text'
  | 'pick_session'
  | 'pick_patient'
  | 'pick_template'
  | 'pick_document'
  | 'pick_ehr'
  | 'pick_file'
  | 'connect_app'
  | 'login_computer';

/**
 * Picker kinds whose option list is fetched CLIENT-SIDE rather than
 * supplied by the agent in ``tool_input.options``. The agent only needs
 * to ask "pick a template / document" — the FE renders the live list
 * (the user's templates, or the linked session's documents). Unlike the
 * other choice/picker kinds these stay themselves when ``options`` is
 * empty (empty is the expected shape). Empty ``choice`` also stays
 * ``choice`` — it must not become a free-text card.
 */
export const SELF_FETCHING_PICKER_KINDS: readonly AskUserKind[] = [
  'pick_session',
  'pick_patient',
  'pick_template',
  'pick_document',
  'pick_ehr',
  'connect_app',
];

export interface AskUserOption {
  id: string;
  label: string;
  description?: string;
}

export interface ParsedAskUserInput {
  question: string;
  kind: AskUserKind;
  options: AskUserOption[];
  maxSelections: number;
  /** Choice-only: whether to show the free-text Other row. */
  allowOther: boolean;
  placeholder: string;
  /** Target session for ``pick_document`` (the agent's runtime session).
   *  The document picker falls back to the composer-linked session when
   *  absent. Ignored by every other kind. */
  sessionId?: string;
}

/**
 * A single step of a (possibly batched) clarification. Identical to
 * ``ParsedAskUserInput`` plus the caller-chosen ``id`` that keys the
 * answer back to the agent in the batched ``answers`` map.
 */
export interface ParsedClarificationQuestion extends ParsedAskUserInput {
  id: string;
}

export interface ResolvedAnswer {
  selected_option_ids: string[];
  text: string | null;
}
